import dayjs from 'dayjs';

/** @typedef {'daily'|'weekly'|'monthly'|'yearly'} RecPattern */
/** @typedef {'noEnd'|'endBy'} RangeMode */

const WEEKDAY_KEY_TO_DAYJS = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
};

const MAX_PREVIEW_DAYS = 731;
const MAX_SCAN_DAYS = 4000;

/**
 * @param {number} y
 * @param {number} m 1-12
 */
function daysInMonth(y, m) {
  return dayjs(`${y}-${String(m).padStart(2, '0')}-01`).daysInMonth();
}

/**
 * @param {number} y
 * @param {number} m 1-12
 * @param {number} ordinal 1-4 first..fourth, 5 = last
 * @param {keyof typeof WEEKDAY_KEY_TO_DAYJS} weekdayKey
 */
function nthWeekdayOfMonth(y, m, ordinal, weekdayKey) {
  const want = WEEKDAY_KEY_TO_DAYJS[weekdayKey];
  const dim = daysInMonth(y, m);
  if (ordinal === 5) {
    for (let d = dim; d >= 1; d -= 1) {
      const dt = dayjs(
        `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
      );
      if (dt.day() === want) return dt;
    }
    return null;
  }
  let seen = 0;
  for (let d = 1; d <= dim; d += 1) {
    const dt = dayjs(
      `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
    );
    if (dt.day() === want) {
      seen += 1;
      if (seen === ordinal) return dt;
    }
  }
  return null;
}

function startOfWeekSun(d) {
  return d.startOf('week');
}

function matchesWeekly(
  d,
  seriesStart,
  intervalWeeks,
  weekDays /* number[] 0-6 */,
) {
  if (d.isBefore(seriesStart, 'day')) return false;
  if (!weekDays.includes(d.day())) return false;
  const w0 = startOfWeekSun(seriesStart);
  const w1 = startOfWeekSun(d);
  const weekDiff = w1.diff(w0, 'week');
  return weekDiff >= 0 && weekDiff % intervalWeeks === 0;
}

function matchesDailyInterval(d, seriesStart, n) {
  if (d.isBefore(seriesStart, 'day')) return false;
  return d.diff(seriesStart, 'day') % n === 0;
}

function matchesDailyWeekday(d, seriesStart) {
  if (d.isBefore(seriesStart, 'day')) return false;
  const dow = d.day();
  return dow >= 1 && dow <= 5;
}

/**
 * Expand occurrence dates (Outlook-style). Caps open-ended series for calendar preview.
 * @param {{
 *   seriesStart: import('dayjs').Dayjs,
 *   pattern: RecPattern,
 *   dailyMode: 'interval'|'weekday',
 *   dailyEveryN: number,
 *   weeklyEveryN: number,
 *   weeklyDays: number[],
 *   monthlyMode: 'dayOfMonth'|'nthWeekday'|'firstOrLastCalendarDay',
 *   monthlyDay: number,
 *   monthlyNth: number,
 *   monthlyWeekday: keyof typeof WEEKDAY_KEY_TO_DAYJS,
 *   monthlyFirstOrLast: 'first'|'last',
 *   yearlyMode: 'date'|'nthWeekday',
 *   yearlyMonth: number,
 *   yearlyDay: number,
 *   yearlyNth: number,
 *   yearlyWeekday: keyof typeof WEEKDAY_KEY_TO_DAYJS,
 *   rangeMode: RangeMode,
 *   rangeEndBy: import('dayjs').Dayjs,
 * }} p
 * @returns {string[]}
 */
export function expandOutlookOccurrenceDates(p) {
  const seriesStart = p.seriesStart.startOf('day');
  if (!seriesStart.isValid()) return [];

  if (
    p.rangeMode === 'endBy' &&
    p.rangeEndBy &&
    p.rangeEndBy.startOf('day').isBefore(seriesStart, 'day')
  ) {
    return [];
  }

  let scanEnd = seriesStart.add(MAX_SCAN_DAYS, 'day');

  if (p.rangeMode === 'endBy') {
    const end = p.rangeEndBy.startOf('day');
    scanEnd = end.isAfter(scanEnd) ? scanEnd : end;
  } else if (p.rangeMode === 'noEnd') {
    scanEnd = seriesStart.add(MAX_PREVIEW_DAYS, 'day');
  }

  const out = [];
  let d = seriesStart.clone();

  while (!d.isAfter(scanEnd) && out.length < 10000) {
    let hit = false;
    const y = d.year();
    const m = d.month() + 1;
    const dom = d.date();

    switch (p.pattern) {
      case 'daily':
        if (p.dailyMode === 'weekday') {
          hit = matchesDailyWeekday(d, seriesStart);
        } else {
          hit = matchesDailyInterval(
            d,
            seriesStart,
            Math.max(1, p.dailyEveryN || 1),
          );
        }
        break;
      case 'weekly':
        hit = matchesWeekly(
          d,
          seriesStart,
          Math.max(1, p.weeklyEveryN || 1),
          Array.isArray(p.weeklyDays) && p.weeklyDays.length
            ? p.weeklyDays
            : [1, 2, 3, 4, 5],
        );
        break;
      case 'monthly': {
        const every = 1;
        const monthDiff =
          (y - seriesStart.year()) * 12 + (d.month() - seriesStart.month());
        if (monthDiff < 0 || monthDiff % every !== 0) {
          hit = false;
          break;
        }
        if (p.monthlyMode === 'firstOrLastCalendarDay') {
          const dim = daysInMonth(y, m);
          if (p.monthlyFirstOrLast === 'last') {
            hit = dom === dim;
          } else {
            hit = dom === 1;
          }
          break;
        }
        if (p.monthlyMode === 'dayOfMonth') {
          const target = Math.min(
            Math.max(1, p.monthlyDay || 1),
            daysInMonth(y, m),
          );
          hit = dom === target;
        } else {
          const nth = Math.min(5, Math.max(1, p.monthlyNth || 1));
          const wk = p.monthlyWeekday || 'MONDAY';
          const occ = nthWeekdayOfMonth(y, m, nth, wk);
          hit = occ && occ.isSame(d, 'day');
        }
        break;
      }
      case 'yearly': {
        if (d.month() + 1 !== (p.yearlyMonth || 1)) {
          hit = false;
          break;
        }
        if (p.yearlyMode === 'date') {
          const target = Math.min(
            Math.max(1, p.yearlyDay || 1),
            daysInMonth(y, p.yearlyMonth || 1),
          );
          hit = dom === target;
        } else {
          const nth = Math.min(5, Math.max(1, p.yearlyNth || 1));
          const wk = p.yearlyWeekday || 'MONDAY';
          const occ = nthWeekdayOfMonth(y, p.yearlyMonth || 1, nth, wk);
          hit = occ && occ.isSame(d, 'day');
        }
        break;
      }
      default:
        hit = false;
    }

    if (hit) {
      const key = d.format('YYYY-MM-DD');
      if (!out.includes(key)) out.push(key);
    }

    d = d.add(1, 'day');
    if (p.rangeMode === 'noEnd' && out.length >= MAX_PREVIEW_DAYS) break;
  }

  return out;
}

export { WEEKDAY_KEY_TO_DAYJS };
